import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function animateCounter(el: HTMLElement, target: string, dur = 1400) {
  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const num = parseFloat(target);
  const prefix = el.dataset.prefix || "";
  const suffix = el.dataset.suffix || "";
  const decimalsAttr = el.dataset.decimals;
  const decimals = decimalsAttr != null ? parseInt(decimalsAttr, 10) : (String(target).includes(".") ? 1 : 0);
  const noLocale = el.dataset.format === "plain";
  if (Number.isNaN(num)) {
    el.textContent = prefix + target + suffix;
    return;
  }
  const fmt = (v: number) => {
    const r = decimals > 0 ? v.toFixed(decimals) : Math.round(v);
    return decimals > 0 || noLocale ? String(r) : Math.round(v).toLocaleString("en-IN");
  };
  if (reduce) {
    el.textContent = prefix + fmt(num) + suffix;
    return;
  }
  const start = performance.now();
  function tick(now: number) {
    const t = Math.min(1, (now - start) / dur);
    const eased = 1 - Math.pow(1 - t, 3);
    const v = num * eased;
    el.textContent = prefix + fmt(v) + suffix;
    if (t < 1) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

/** IntersectionObserver-driven reveal + counter animation, matching the design's behavior. */
export function useReveal() {
  const { pathname } = useLocation();
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".reveal, .mask-reveal, [data-count]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const target = e.target as HTMLElement;
          target.classList.add("in");
          if (target.dataset && target.dataset.count) {
            animateCounter(target, target.dataset.count);
          }
          target.querySelectorAll<HTMLElement>("[data-count]").forEach((c) => {
            if (!c.dataset._counted) {
              c.dataset._counted = "1";
              animateCounter(c, c.dataset.count!);
            }
          });
          io.unobserve(target);
        });
      },
      { threshold: 0.1 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);
}
